import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { shared, auth } from '../utils/api';
import { Printer, Plus, Clock, Users, ChefHat, Loader, Sparkles, Wrench, ArrowLeft, ExternalLink } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

// Detect if running inside an in-app browser (WebView) where Google OAuth is blocked
const isInAppBrowser = () => {
  const ua = navigator.userAgent || '';
  return /FBAN|FBAV|Instagram|Twitter|Line|Snapchat|LinkedIn|Pinterest|TikTok/i.test(ua) ||
    (/AppleWebKit/i.test(ua) && !/Safari/i.test(ua)) ||
    (/wv\)|Version\/[\d.]+.*Chrome\/[\d.]+/i.test(ua) && /Android/i.test(ua));
};

// Helper to strip markdown bold formatting from text
const stripMarkdown = (text) => {
  if (!text) return text;
  return text.replace(/\*\*([^*]+)\*\*/g, '$1');
};

// Timer launch helper - detects platform and launches native timer
const canLaunchNativeTimer = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  if (/android/i.test(userAgent)) return 'android';
  if (navigator.platform && navigator.platform.indexOf('Win') !== -1) return 'windows';
  // iOS and macOS - no reliable native timer URL scheme from web
  return null;
};

const launchNativeTimer = (minutes, label) => {
  const platform = canLaunchNativeTimer();
  const seconds = minutes * 60;
  const truncatedLabel = label.length > 50 ? label.substring(0, 47) + '...' : label;
  
  try {
    switch (platform) {
      case 'android':
        window.location.href = `intent://timer?seconds=${seconds}&message=${encodeURIComponent(truncatedLabel)}#Intent;scheme=android-app;package=com.google.android.deskclock;end`;
        break;
      case 'windows':
        window.location.href = `ms-clock:timer?minutes=${minutes}&label=${encodeURIComponent(truncatedLabel)}`;
        break;
      default:
        return false;
    }
    return true;
  } catch (e) {
    console.error('Failed to launch timer:', e);
    return false;
  }
};

const SharedRecipeView = ({ variant = false }) => {
  const { sourceRecipeId, recipeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromBuzzHistory = location.state?.fromBuzzHistory || false;
  const fromHive = location.state?.fromHive || null;
  const hiveName = location.state?.hiveName || null;
  const hiveIcon = location.state?.hiveIcon || '🐝';
  const fromFeed = location.state?.fromFeed || false;
  // Track if user navigated here from within the app (vs opening a direct link)
  const hasNavigationState = fromHive || fromFeed || fromBuzzHistory;
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buzzCount, setBuzzCount] = useState(0);
  const [addingToBox, setAddingToBox] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showWebViewPrompt, setShowWebViewPrompt] = useState(false);

  // Check if user is logged in
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await auth.getCurrentUser();
        setIsLoggedIn(true);
        // Store session_token in localStorage for API calls (Safari ITP fix)
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
      } catch {
        setIsLoggedIn(false);
      } finally {
        setCheckingAuth(false);
      }
    };
    checkAuth();
  }, []);

  // Fetch shared recipe or variant
  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        let response;
        if (variant && recipeId) {
          // Fetch specific user's variant
          response = await shared.getVariant(recipeId);
        } else if (sourceRecipeId) {
          // Fetch source recipe
          response = await shared.getRecipe(sourceRecipeId);
        }
        
        if (response) {
          setRecipe(response.data);
          
          // Fetch buzz count using source_recipe_id
          const srcId = response.data.source_recipe_id || sourceRecipeId;
          if (srcId) {
            const buzzResponse = await shared.getBuzzHistory(srcId);
            setBuzzCount(buzzResponse.data.count || 0);
          }
        }
      } catch (err) {
        console.error('Failed to fetch recipe:', err);
        setError('Recipe not found or no longer available');
      } finally {
        setLoading(false);
      }
    };
    
    if (sourceRecipeId || recipeId) {
      fetchRecipe();
    }
  }, [sourceRecipeId, recipeId, variant]);

  const handleAddToBox = async () => {
    if (!isLoggedIn) {
      // If in a WebView, show prompt to open in Safari/app instead of navigating to broken login
      if (isInAppBrowser()) {
        setShowWebViewPrompt(true);
        return;
      }
      // Store the source_recipe_id to auto-add after login
      // Use the recipe's source_recipe_id (canonical ID) just like Hive does
      const idToStore = recipe?.source_recipe_id || sourceRecipeId || recipeId;
      sessionStorage.setItem('autoAddSharedRecipe', idToStore);
      sessionStorage.setItem('intendedUrl', window.location.pathname);
      navigate('/login');
      return;
    }
    
    setAddingToBox(true);
    try {
      // Always use source_recipe_id from the recipe data (canonical ID)
      // This matches how HiveDetailPage does it and what the backend expects
      const idToAdd = recipe?.source_recipe_id || sourceRecipeId || recipeId;
      const response = await shared.addToBox(idToAdd);
      // Navigate to the new recipe
      navigate(`/recipe/${response.data.recipe_id}`);
    } catch (err) {
      console.error('Failed to add to recipe box:', err);
      alert('Failed to add recipe. Please try again.');
    } finally {
      setAddingToBox(false);
    }
  };

  // Direct print - opens print dialog immediately
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to print');
      return;
    }

    const fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';

    const ingredientsList = recipe.ingredients?.map(ing => {
      const hasQty = ing.quantity && ing.quantity !== 'null' && String(ing.quantity).trim() !== '';
      const hasUnit = ing.unit && ing.unit !== 'null' && ing.unit.trim() !== '';
      const qtyUnit = [hasQty ? ing.quantity : '', hasUnit ? ing.unit : ''].filter(Boolean).join(' ');
      const prep = ing.preparation ? ` (${ing.preparation})` : '';
      return `<li>${qtyUnit ? `<strong>${qtyUnit}</strong> ` : ''}${ing.name}${prep}</li>`;
    }).join('') || '';

    const instructionsList = recipe.instructions?.map(inst => {
      const duration = inst.duration_minutes ? ` <span style="color: #666; font-size: 0.9em;">(${inst.duration_minutes} min)</span>` : '';
      return `<li><strong>${inst.step_number}.</strong> ${inst.text}${duration}</li>`;
    }).join('') || '';

    const tipsList = recipe.recipe_tips?.map(tip => `<li>${tip}</li>`).join('') || '';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${recipe.title} - Recipe</title>
        <style>
          body { font-family: ${fontFamily}; max-width: 1000px; margin: 0 auto; padding: 40px 20px; color: #333; font-size: 14px; }
          h1 { font-size: 22px; margin-bottom: 8px; color: #222; border-bottom: 2px solid #d97706; padding-bottom: 8px; }
          .meta { color: #666; margin-bottom: 16px; font-size: 13px; }
          .meta span { margin-right: 16px; }
          .description { font-style: italic; color: #555; margin-bottom: 16px; font-size: 13px; }
          .source { background: #fff8e7; padding: 8px 12px; border-radius: 6px; margin-bottom: 16px; font-size: 12px; }
          .source a { color: #d97706; }
          .tweaks { margin-top: 8px; font-size: 12px; color: #666; }
          .recipe-grid { display: grid; grid-template-columns: 1fr 2fr; gap: 32px; }
          .ingredients-col h2, .instructions-col h2 { font-size: 16px; color: #d97706; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
          .ingredients-col ul { list-style: none; padding: 0; margin: 0; }
          .ingredients-col li { padding: 4px 0; border-bottom: 1px dotted #eee; font-size: 13px; }
          .instructions-col ol { padding-left: 0; margin: 0; list-style: none; }
          .instructions-col li { margin-bottom: 10px; line-height: 1.5; font-size: 13px; }
          .tips { margin-top: 20px; padding-top: 16px; border-top: 1px solid #eee; }
          .tips h2 { font-size: 14px; color: #666; }
          .tips ul { list-style: disc; padding-left: 20px; font-size: 12px; color: #666; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>${recipe.title}</h1>
        <div class="meta">
          ${recipe.prep_time ? `<span>Prep: ${recipe.prep_time} min</span>` : ''}
          ${recipe.cook_time ? `<span>Cook: ${recipe.cook_time} min</span>` : ''}
          ${recipe.servings ? `<span>Servings: ${recipe.servings}</span>` : ''}
          ${recipe.difficulty_level ? `<span>Difficulty: ${recipe.difficulty_level}</span>` : ''}
        </div>
        ${recipe.description ? `<p class="description">${recipe.description}</p>` : ''}
        ${recipe.source_url || (recipe.tweaks && recipe.tweaks.length > 0) ? `
          <div class="source">
            ${recipe.source_url ? `<span>✨ Inspired by: <a href="${recipe.source_url}" target="_blank">${(() => { try { return new URL(recipe.source_url).hostname; } catch { return recipe.source_url; } })()}</a></span>` : ''}
            ${recipe.tweaks && recipe.tweaks.length > 0 ? `<div class="tweaks">🔧 Tweaks: ${recipe.tweaks.join('; ')}</div>` : ''}
          </div>
        ` : ''}
        
        <div class="recipe-grid">
          <div class="ingredients-col">
            <h2>Ingredients</h2>
            <ul>${ingredientsList}</ul>
          </div>
          <div class="instructions-col">
            <h2>Preparation</h2>
            <ol>${instructionsList}</ol>
            ${tipsList ? `<div class="tips"><h2>Tips & Tricks</h2><ul>${tipsList}</ul></div>` : ''}
          </div>
        </div>
        ${recipe.source_owner_name ? `<p style="margin-top: 20px; font-size: 12px; color: #999;">Shared by ${recipe.source_owner_name} via Honey</p>` : ''}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <LoadingSpinner message="Loading recipe..." fullScreen />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-amber-50 p-4">
        <div className="text-center">
          <div className="text-6xl mb-4">🍯</div>
          <h2 className="text-xl font-bold mb-2">Recipe Not Found</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
          >
            Go to Honey
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Dynamic meta tags for link previews */}
      <Helmet>
        <title>{recipe?.title ? `${recipe.title} | Honey` : 'Shared Recipe | Honey'}</title>
        <meta name="description" content={recipe?.description || `Check out this recipe on Honey!`} />
        <meta property="og:title" content={recipe?.title || 'Shared Recipe'} />
        <meta property="og:description" content={recipe?.description || `A delicious recipe shared on Honey`} />
        <meta property="og:type" content="article" />
      </Helmet>
      
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          {/* Row 1: Icons */}
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              {/* Only show back button if user navigated here from within the app.
                  If user opened this link directly (e.g., from text message or social media), 
                  there's nothing to go back to, so hide the arrow. */}
              {hasNavigationState && (
                <button
                  onClick={() => {
                    if (fromHive) {
                      navigate(`/hive/${fromHive}`);
                    } else {
                      navigate(-1);
                    }
                  }}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
                  title={fromHive ? `Back to ${hiveName}` : 'Back'}
                >
                  <ArrowLeft size={20} />
                </button>
              )}
              {fromHive ? (
                <button
                  onClick={() => navigate(`/hive/${fromHive}`)}
                  className="flex items-center gap-1 text-amber-600 hover:text-amber-700"
                >
                  <span className="text-xl">{hiveIcon}</span>
                  <span className="text-sm font-medium truncate max-w-[120px]">{hiveName}</span>
                </button>
              ) : (
                <span className="text-2xl">🐝</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrint}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                title="Print"
                data-testid="print-btn"
              >
                <Printer size={20} />
              </button>
              {/* Hide buzz count on shared/view-only recipes - this is not the user's recipe */}
              {/* Buzz history only makes sense on the user's own recipe */}
            </div>
          </div>
          {/* Row 2: Full title */}
          <h1 className="text-xl font-bold text-gray-900">{recipe?.title}</h1>
          {/* Show variant owner (for variants) or source owner (for original) */}
          {recipe?.variant_owner_name ? (
            <p className="text-sm text-gray-500">{recipe.variant_owner_name}'s version</p>
          ) : recipe?.source_owner_name && (
            <p className="text-sm text-gray-500">Shared by {recipe.source_owner_name}</p>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-28">
        {/* Recipe Image */}
        {recipe?.images?.[0] && (
          <div className="mb-6 rounded-xl overflow-hidden">
            <img 
              src={recipe.images[0].startsWith('data:') ? recipe.images[0] : `data:image/jpeg;base64,${recipe.images[0]}`}
              alt={recipe.title}
              className="w-full h-64 object-cover"
            />
          </div>
        )}

        {/* Description */}
        {recipe?.description && (
          <p className="text-gray-600 mb-4 italic">{recipe.description}</p>
        )}

        {/* Meta Info */}
        <div className="flex flex-wrap gap-4 mb-4 text-sm text-gray-600">
          {recipe?.prep_time && (
            <div className="flex items-center gap-1">
              <Clock size={16} />
              <span>Prep: {recipe.prep_time} min</span>
            </div>
          )}
          {recipe?.cook_time && (
            <div className="flex items-center gap-1">
              <Clock size={16} />
              <span>Cook: {recipe.cook_time} min</span>
            </div>
          )}
          {recipe?.servings && (
            <div className="flex items-center gap-1">
              <Users size={16} />
              <span>Servings: {recipe.servings}</span>
            </div>
          )}
          {recipe?.difficulty_level && (
            <div className="flex items-center gap-1">
              <ChefHat size={16} />
              <span>Difficulty: {recipe.difficulty_level}</span>
            </div>
          )}
        </div>

        {/* Source & Tweaks */}
        {(recipe?.source_url || (recipe?.tweaks && recipe.tweaks.length > 0)) && (
          <div className="bg-amber-50 rounded-lg p-4 mb-6 border border-amber-100">
            {recipe.source_url && (
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={16} className="text-amber-600" />
                <span className="text-sm text-gray-700">Inspired by: </span>
                <a 
                  href={recipe.source_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-amber-600 hover:underline"
                >
                  {(() => {
                    try {
                      return new URL(recipe.source_url).hostname;
                    } catch {
                      return recipe.source_url;
                    }
                  })()}
                </a>
              </div>
            )}
            {recipe.tweaks && recipe.tweaks.length > 0 && (
              <div className="flex items-start gap-2">
                <Wrench size={16} className="text-gray-500 mt-0.5" />
                <div>
                  <span className="text-sm text-gray-700">Tweaks:</span>
                  <ul className="text-sm text-gray-600 mt-1">
                    {recipe.tweaks.map((tweak, idx) => (
                      <li key={idx}>- {tweak}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {/* WEB-STYLE LAYOUT: Side by side on desktop, stacked on mobile */}
        <div className="grid md:grid-cols-3 gap-8">
          {/* Ingredients Column */}
          <div className="md:col-span-1">
            <h2 className="text-lg font-bold text-amber-600 mb-4 uppercase tracking-wide border-b-2 border-amber-600 pb-2">
              Ingredients
            </h2>
            <ul className="space-y-2">
              {recipe?.ingredients?.map((ing, idx) => (
                <li key={idx} className="flex items-start gap-2 py-1 border-b border-gray-100">
                  <span className="text-gray-800">
                    {(ing.quantity || ing.unit) && (
                      <strong>{[ing.quantity, ing.unit].filter(Boolean).join(' ')} </strong>
                    )}
                    {ing.name}
                    {ing.preparation && <span className="text-gray-500"> ({ing.preparation})</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Instructions Column */}
          <div className="md:col-span-2">
            <h2 className="text-lg font-bold text-amber-600 mb-4 uppercase tracking-wide border-b-2 border-amber-600 pb-2">
              Preparation
            </h2>
            <ol className="space-y-4">
              {recipe?.instructions?.map((step, idx) => (
                <li key={idx} className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center font-bold flex-shrink-0">
                    {step.step_number || idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-800">{stripMarkdown(step.text)}</p>
                    {step.duration_minutes && (
                      canLaunchNativeTimer() ? (
                        <button
                          onClick={() => launchNativeTimer(step.duration_minutes, `Step ${step.step_number || idx + 1}: ${stripMarkdown(step.text).substring(0, 50)}`)}
                          className="text-sm text-amber-600 hover:text-amber-700 mt-1 flex items-center gap-1 cursor-pointer transition-colors"
                          title={`Set ${step.duration_minutes} min timer`}
                        >
                          <Clock size={14} />
                          <span className="underline">{step.duration_minutes} min</span>
                        </button>
                      ) : (
                        <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                          <Clock size={14} />
                          {step.duration_minutes} min
                        </p>
                      )
                    )}
                  </div>
                </li>
              ))}
            </ol>

            {/* Tips */}
            {recipe?.recipe_tips && recipe.recipe_tips.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-md font-semibold text-gray-700 mb-3">Tips & Tricks</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  {recipe.recipe_tips.map((tip, idx) => (
                    <li key={idx} className="flex gap-2">
                      <span className="text-amber-600">💡</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Fixed Bottom Button - Show for:
          1. Source recipes (not variants) when navigating in-app, OR
          2. ANY shared recipe when opened via direct link (no navigation state) */}
      {(!variant || !hasNavigationState) && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-50">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={handleAddToBox}
              disabled={addingToBox || checkingAuth}
              className="w-full flex items-center justify-center gap-2 py-3 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 transition-colors disabled:opacity-50"
              data-testid="add-to-box-btn"
            >
              {addingToBox ? (
                <Loader className="animate-spin" size={20} />
              ) : (
                <Plus size={20} />
              )}
              Add to My Recipe Box
            </button>
          </div>
        </div>
      )}

      {/* WebView prompt - shown when user tries to sign in from an in-app browser */}
      {showWebViewPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[100] p-4" onClick={() => setShowWebViewPrompt(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-4">
              <img src="/HoneyCookingCompanion.svg" alt="Honey" className="w-32 h-auto mx-auto mb-3" />
              <h3 className="text-lg font-bold text-gray-900">Open in Safari to sign in</h3>
              <p className="text-sm text-gray-600 mt-2">
                Google sign-in doesn't work in this browser. Tap the share button below and choose <strong>"Open in Safari"</strong> to add this recipe.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-3 mb-4">
              <p className="text-xs text-gray-500 text-center">
                Look for the <ExternalLink size={12} className="inline" /> share icon or <strong>Open in Safari</strong> in your browser menu
              </p>
            </div>

            <a
              href={`https://apps.apple.com/app/id6761855596`}
              className="w-full flex items-center justify-center gap-2 py-3 bg-gray-900 text-white rounded-xl font-medium mb-3"
            >
              Or open in the Honey app
            </a>
            
            <button
              onClick={() => setShowWebViewPrompt(false)}
              className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedRecipeView;
